'use client';

import React, { useState, useMemo } from 'react';
import ShareGreetingDialog from './ShareGreetingDialog';

interface GreetingCardEditorProps {
    isOpen: boolean;
    onClose: () => void;
    /** The base URL of the current project page (e.g. https://domain.com/sector/project) */
    baseProjectUrl: string;
    /** Locale for language support: 'vn' | 'en' */
    locale?: string;
}

export default function GreetingCardEditor({ isOpen, onClose, baseProjectUrl, locale = 'vn' }: GreetingCardEditorProps) {
    const [title, setTitle] = useState('');
    const [name, setName] = useState('');
    const [showShareDialog, setShowShareDialog] = useState(false);

    // Check if locale is English
    const isEnglish = locale?.toLowerCase() === 'en';

    // Translations
    const t = {
        title: isEnglish ? 'Send New Year Card' : 'Gửi Thiếp Tân Xuân',
        recipientTitle: isEnglish ? 'Recipient Title' : 'Danh xưng người nhận',
        titlePlaceholder: isEnglish ? 'Mr, Mrs, Dear, Friend...' : 'Anh (Mr), Chị (Mrs), Bạn, Em...',
        recipientName: isEnglish ? 'Recipient Name' : 'Tên người nhận',
        namePlaceholder: isEnglish ? 'e.g. John' : 'Ví dụ: Hào',
        share: isEnglish ? 'Share' : 'Chia sẻ',
        close: isEnglish ? 'Close' : 'Đóng',
    };


    // Build the share URL
    const shareUrl = useMemo(() => {
        const url = new URL(baseProjectUrl);
        url.searchParams.set('greeting', 'true');
        if (title.trim()) url.searchParams.set('title', title.trim());
        if (name.trim()) url.searchParams.set('name', name.trim());
        return url.toString();
    }, [baseProjectUrl, title, name]);

    const canShare = title.trim() || name.trim();

    const handleShare = () => {
        if (!canShare) return;
        setShowShareDialog(true);
    };

    const handleClose = () => {
        setTitle('');
        setName('');
        setShowShareDialog(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="greeting-editor-overlay" onClick={handleClose}>
                <div className="greeting-editor-card" onClick={(e) => e.stopPropagation()}>
                    {/* Close button */}
                    <button className="greeting-editor-close" onClick={handleClose} aria-label={t.close}>
                        ✕
                    </button>

                    {/* Card frame with khung_thiep */}
                    <div className="greeting-editor-frame">
                        <img
                            src="/images/khung_thiep.png"
                            alt={isEnglish ? 'Card frame' : 'Khung thiệp'}
                            draggable={false}
                        />
                        {/* Logo inside the white area of the frame */}
                        <img
                            src="/images/logodoc.png"
                            alt="Logo"
                            className="greeting-editor-logo"
                            draggable={false}
                        />
                        {/* Title overlay on frame */}
                        <h2 className="greeting-editor-title-overlay">{t.title}</h2>
                    </div>

                    {/* Form body */}
                    <div className="greeting-editor-body">

                        {/* Danh xưng - text input */}
                        <div className="greeting-editor-field">
                            <label htmlFor="greeting-title">{t.recipientTitle}</label>
                            <input
                                id="greeting-title"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={t.titlePlaceholder}
                                maxLength={30}
                            />
                        </div>

                        {/* Tên người nhận */}
                        <div className="greeting-editor-field">
                            <label htmlFor="greeting-name">{t.recipientName}</label>
                            <input
                                id="greeting-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t.namePlaceholder}
                                maxLength={50}
                            />
                        </div>

                        {/* Share button */}
                        <button
                            className="greeting-editor-share-btn"
                            onClick={handleShare}
                            disabled={!canShare}
                        >
                            🎊 {t.share}
                        </button>
                    </div>
                </div>
            </div>

            {/* Share Dialog */}
            <ShareGreetingDialog
                isOpen={showShareDialog}
                onClose={() => setShowShareDialog(false)}
                shareUrl={shareUrl}
                locale={locale}
            />
        </>
    );
}
